import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

// Unique key combines id + kind so a bundle and product with same id won't clash
const keyOf = (item) => `${item.kind || "product"}::${item.id}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("småstund_cart");
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("småstund_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, kind = "product") => {
    setItems((prev) => {
      const newItem = {
        id: product.id,
        kind,
        slug: product.slug,
        title_no: product.title_no,
        title_en: product.title_en,
        price_nok: product.price_nok,
        image_url: product.image_url,
        quantity: 1,
      };
      const k = keyOf(newItem);
      const existing = prev.find((p) => keyOf(p) === k);
      if (existing) {
        return prev.map((p) =>
          keyOf(p) === k ? { ...p, quantity: p.quantity + 1 } : p
        );
      }
      return [...prev, newItem];
    });
  };

  const addBundle = (bundle) => addItem(bundle, "bundle");

  const removeItem = (id, kind = "product") => {
    setItems((prev) => prev.filter((p) => keyOf(p) !== keyOf({ id, kind })));
  };

  const updateQty = (id, qty, kind = "product") => {
    if (qty <= 0) return removeItem(id, kind);
    setItems((prev) =>
      prev.map((p) =>
        keyOf(p) === keyOf({ id, kind }) ? { ...p, quantity: qty } : p
      )
    );
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price_nok * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const hasItem = (id, kind = "product") =>
    items.some((p) => keyOf(p) === keyOf({ id, kind }));

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        addBundle,
        removeItem,
        updateQty,
        clear,
        subtotal,
        count,
        hasItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
